import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { IconComponent } from './icon.component';
import { type CfConditionKind, type CfRuleDraft, type CfRuleView, type CfStyle } from '@sharedlib/sheets-editor/cf-rules';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { UI_REASONS } from './ui-reasons';
import { asText, setFlag, setOneOf, setText, wrongKind, type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import type { SheetsUiValue } from '@sharedlib/common/sheets/ui-commands';

/** Stitch sheet only (AM-15). */
const BAR_COLOURS = ['#1B5FFF', '#339995', '#8C4CD1', '#FF9500'] as const;
const FILLS = ['#EBF1FF', '#D5F2EE', '#FFEDD5', '#F2EBFC'] as const;
/** Scale endpoints, Stitch sheet only. */
const SCALE_LOW = ['#EBF1FF', '#F2FBFA', '#FFF7ED'] as const;
const SCALE_MID = ['#84ABFF', '#78D0C8', '#FCBA75'] as const;
const SCALE_HIGH = ['#1B5FFF', '#277A79', '#E9590D'] as const;
/** Icon groups the engine ships — the panel offers the three-step sets. */
const ICON_GROUPS = ['3Arrows', '3ArrowsGray', '3Triangles', '3TrafficLights1', '3Signs'] as const;

/**
 * Conditional-formatting panel (AM-29, option B), laid out per mockup frame 05.
 *
 * Emits a draft only; the host applies it through the engine's public API with the same
 * calls as Stitchy's ops 38-42, so one Save rule is one undoable change.
 */
@Component({
  selector: 'app-cf-panel',
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./sheets-panels.css', './sheets-buttons.css'],
  templateUrl: './cf-panel.component.html',
})
export class CfPanelComponent implements UiInspector {
  /** NFR-8: the panel's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  @Output() public readonly save = new EventEmitter<CfRuleDraft>();
  @Output() public readonly cancelled = new EventEmitter<void>();
  @Output() public readonly removeRule = new EventEmitter<string>();

  /** The active sheet's rules, from the grid (cf-rules.ts); the first wins. */
  protected readonly rules = signal<readonly CfRuleView[]>([]);
  @Input() public set sheetRules(rules: readonly CfRuleView[]) {
    this.rules.set(rules);
    // A rule deleted elsewhere (undo, Stitchy) is no longer the one being edited.
    if (this.selectedId() && !rules.some((r) => r.id === this.selectedId())) this.startNew();
  }
  /** The rule being edited, or null for a new one. */
  protected readonly selectedId = signal<string | null>(null);

  /** Where a new rule goes: the selection when the panel opened. */
  private newRange = '';
  @Input() public set initialRange(range: string) {
    this.newRange = range;
    if (!this.selectedId()) this.range.set(range);
  }

  protected readonly range = signal('');
  protected readonly style = signal<CfStyle>('highlight');
  protected readonly shortest = signal('Lowest');
  protected readonly longest = signal('Highest');
  protected readonly barColour = signal<string>(BAR_COLOURS[0]);
  protected readonly negativeColour = signal('#E40000');
  protected readonly gradient = signal(true);
  protected readonly barOnly = signal(false);
  protected readonly condKind = signal<CfConditionKind>('number');
  protected readonly value2 = signal('');
  protected readonly timePeriod = signal('today');
  protected readonly formula = signal('');
  protected readonly rankBottom = signal(false);
  protected readonly rankPercent = signal(false);
  protected readonly rankValue = signal('10');
  protected readonly operator = signal<string>('greaterThan');
  protected readonly value = signal('');
  protected readonly fill = signal<string>(FILLS[0]);
  protected readonly textColour = signal('#154CCC');
  protected readonly scaleMin = signal<string>(SCALE_LOW[0]);
  protected readonly scaleMid = signal<string>(SCALE_MID[0]);
  protected readonly scaleMax = signal<string>(SCALE_HIGH[0]);
  protected readonly scaleThreePoint = signal(true);
  protected readonly iconGroup = signal<string>(ICON_GROUPS[0]);
  protected readonly iconShowValue = signal(true);
  protected readonly iconGroups = ICON_GROUPS;

  /**
   * The engine's highlight rule has eight sub-types and the builder exposes all of
   * them, so the panel offers all of them (AM-29 parity). `containsErrors` and
   * `notContainsErrors` exist in CFTextOperator but have no builder method, so
   * they are absent rather than offered and silently dropped.
   */
  protected readonly condKinds = [
    { key: 'number' as const, label: SHEETS_STRINGS['is a number that'] },
    { key: 'text' as const, label: SHEETS_STRINGS['contains text that'] },
    { key: 'date' as const, label: SHEETS_STRINGS['holds a date in'] },
    { key: 'empty' as const, label: SHEETS_STRINGS['is empty'] },
    { key: 'notEmpty' as const, label: SHEETS_STRINGS['is not empty'] },
    { key: 'average' as const, label: SHEETS_STRINGS['compares to the average'] },
    { key: 'rank' as const, label: SHEETS_STRINGS['ranks in the top or bottom'] },
    { key: 'unique' as const, label: SHEETS_STRINGS['holds a unique value'] },
    { key: 'duplicate' as const, label: SHEETS_STRINGS['holds a duplicate value'] },
    { key: 'formula' as const, label: SHEETS_STRINGS['satisfies a formula'] },
  ];

  private readonly numberOperators = [
    { key: 'greaterThan', label: SHEETS_STRINGS['is greater than'] },
    { key: 'greaterThanOrEqual', label: SHEETS_STRINGS['is greater than or equal to'] },
    { key: 'lessThan', label: SHEETS_STRINGS['is less than'] },
    { key: 'lessThanOrEqual', label: SHEETS_STRINGS['is less than or equal to'] },
    { key: 'equal', label: SHEETS_STRINGS['is equal to'] },
    { key: 'notEqual', label: SHEETS_STRINGS['is not equal to'] },
    { key: 'between', label: SHEETS_STRINGS['is between'] },
    { key: 'notBetween', label: SHEETS_STRINGS['is not between'] },
  ];

  private readonly textOperators = [
    { key: 'containsText', label: SHEETS_STRINGS['contains'] },
    { key: 'notContainsText', label: SHEETS_STRINGS['does not contain'] },
    { key: 'beginsWith', label: SHEETS_STRINGS['begins with'] },
    { key: 'endsWith', label: SHEETS_STRINGS['ends with'] },
    { key: 'equal', label: SHEETS_STRINGS['is exactly'] },
  ];

  /** setAverage accepts six of the eight number operators; between/notBetween are not among them. */
  private readonly averageOperators = this.numberOperators.filter((o) => o.key !== 'between' && o.key !== 'notBetween');

  protected readonly timePeriods = [
    { key: 'today', label: SHEETS_STRINGS['Today'] },
    { key: 'yesterday', label: SHEETS_STRINGS['Yesterday'] },
    { key: 'tomorrow', label: SHEETS_STRINGS['Tomorrow'] },
    { key: 'last7Days', label: SHEETS_STRINGS['The last 7 days'] },
    { key: 'thisWeek', label: SHEETS_STRINGS['This week'] },
    { key: 'lastWeek', label: SHEETS_STRINGS['Last week'] },
    { key: 'nextWeek', label: SHEETS_STRINGS['Next week'] },
    { key: 'thisMonth', label: SHEETS_STRINGS['This month'] },
    { key: 'lastMonth', label: SHEETS_STRINGS['Last month'] },
    { key: 'nextMonth', label: SHEETS_STRINGS['Next month'] },
  ];

  protected readonly styles = [
    { key: 'highlight' as const, label: SHEETS_STRINGS['Highlight'] },
    { key: 'databar' as const, label: SHEETS_STRINGS['Data bar'] },
    { key: 'scale' as const, label: SHEETS_STRINGS['Scale'] },
    { key: 'icons' as const, label: SHEETS_STRINGS['Icons'] },
  ];

  protected pick(e: Event): string {
    return (e.target as HTMLSelectElement).value;
  }
  protected setRange(e: Event): void {
    this.range.set((e.target as HTMLInputElement).value);
  }
  protected setValue(e: Event): void {
    this.value.set((e.target as HTMLInputElement).value);
  }
  protected operatorsFor(): { key: string; label: string }[] {
    switch (this.condKind()) {
      case 'number':
        return this.numberOperators;
      case 'text':
        return this.textOperators;
      case 'average':
        return this.averageOperators;
      default:
        return [];
    }
  }
  protected takesValue(): boolean {
    const k = this.condKind();
    return k === 'number' || k === 'text';
  }
  protected takesTwoValues(): boolean {
    return this.condKind() === 'number' && (this.operator() === 'between' || this.operator() === 'notBetween');
  }
  protected setCondKind(e: Event): void {
    this.pickCondKind((e.target as HTMLSelectElement).value as CfConditionKind);
  }

  /**
   * §11.6.3 #27: the rule type a toolbar entry opens the panel on ("Highlight cell rules",
   * "Top or bottom rules", …). Null, "Manage rules", leaves the panel as it is.
   */
  @Input() public set preset(type: string | null) {
    if (type) this.startNew();
    const kinds: Readonly<Record<string, CfConditionKind>> = { highlight: 'number', rank: 'rank', average: 'average', formula: 'formula' };
    if (type && type in kinds) {
      this.style.set('highlight');
      this.pickCondKind(kinds[type]);
    } else if (type === 'scale' || type === 'databar' || type === 'icons') {
      this.style.set(type);
    }
  }

  private pickCondKind(next: CfConditionKind): void {
    this.condKind.set(next);
    // Operators are per-kind, so an operator carried over from the previous kind
    // would be looked up in the wrong map and build nothing.
    const first = this.operatorsFor()[0];
    if (first) this.operator.set(first.key as never);
  }
  protected setTimePeriod(e: Event): void {
    this.timePeriod.set((e.target as HTMLSelectElement).value);
  }
  protected setFormula(e: Event): void {
    this.formula.set((e.target as HTMLInputElement).value);
  }
  protected setValue2(e: Event): void {
    this.value2.set((e.target as HTMLInputElement).value);
  }
  protected setRankValue(e: Event): void {
    this.rankValue.set((e.target as HTMLInputElement).value);
  }

  protected setOperator(e: Event): void {
    this.operator.set((e.target as HTMLSelectElement).value);
  }
  protected cycleBar(): void {
    this.barColour.set(BAR_COLOURS[(BAR_COLOURS.indexOf(this.barColour() as never) + 1) % BAR_COLOURS.length]);
  }
  protected cycleNegative(): void {
    this.negativeColour.set(this.negativeColour() === '#E40000' ? '#FF9500' : '#E40000');
  }
  protected cycleFill(): void {
    this.fill.set(FILLS[(FILLS.indexOf(this.fill() as never) + 1) % FILLS.length]);
  }
  protected cycleText(): void {
    this.textColour.set(this.textColour() === '#154CCC' ? '#0F1F33' : '#154CCC');
  }
  protected cycleScale(which: 'min' | 'mid' | 'max'): void {
    const [list, sig] =
      which === 'min'
        ? [SCALE_LOW, this.scaleMin]
        : which === 'mid'
          ? [SCALE_MID, this.scaleMid]
          : [SCALE_HIGH, this.scaleMax];
    const arr = list as readonly string[];
    sig.set(arr[(arr.indexOf(sig()) + 1) % arr.length]);
  }

  protected scalePreview(): string {
    return this.scaleThreePoint()
      ? `linear-gradient(to right, ${this.scaleMin()}, ${this.scaleMid()}, ${this.scaleMax()})`
      : `linear-gradient(to right, ${this.scaleMin()}, ${this.scaleMax()})`;
  }

  /** "Add rule": a new rule on the selection, the form as it starts. */
  protected addRule(): void {
    this.startNew();
  }

  private startNew(): void {
    this.selectedId.set(null);
    // A rule with no fill or text colour leaves '' behind; a new rule starts with the colours.
    if (!this.fill()) this.fill.set(FILLS[0]);
    if (!this.textColour()) this.textColour.set('#154CCC');
    this.range.set(this.newRange);
    this.value.set('');
    this.value2.set('');
    this.formula.set('');
  }

  /** A rule in the list: loaded into the form, and Save replaces it. */
  protected selectRule(rule: CfRuleView): void {
    this.selectedId.set(rule.id);
    const f = rule.form;
    const set = <T>(sig: { set(v: T): void }, v: T | undefined) => v !== undefined && sig.set(v);
    set(this.range, f.range);
    set(this.style, f.style);
    set(this.shortest, f.shortest);
    set(this.longest, f.longest);
    set(this.barColour, f.barColour);
    set(this.negativeColour, f.negativeColour);
    set(this.gradient, f.gradient);
    set(this.barOnly, f.barOnly);
    if (f.condKind) this.pickCondKind(f.condKind);
    set(this.operator, f.operator);
    set(this.value, f.value);
    set(this.value2, f.value2);
    set(this.timePeriod, f.timePeriod);
    set(this.formula, f.formula);
    set(this.rankBottom, f.rankBottom);
    set(this.rankPercent, f.rankPercent);
    set(this.rankValue, f.rankValue);
    set(this.fill, f.fill);
    set(this.textColour, f.textColour);
    set(this.scaleMin, f.scaleMin);
    set(this.scaleMid, f.scaleMid);
    set(this.scaleMax, f.scaleMax);
    set(this.scaleThreePoint, f.scaleThreePoint);
    set(this.iconGroup, f.iconGroup);
    set(this.iconShowValue, f.iconShowValue);
  }

  /** The rule is deleted from the sheet; the host gives the panel the new list. */
  protected deleteRule(id: string, e: Event): void {
    e.stopPropagation();
    if (this.selectedId() === id) this.startNew();
    this.removeRule.emit(id);
  }

  // ------------------------------------------- sheets_ui (MCP contract, 6 Oct: "set fields … apply/cancel")

  public uiFields(): Readonly<Record<string, unknown>> {
    return {
      rules: this.rules().map((r) => ({ id: r.id, name: r.name, range: r.range, style: r.style })),
      rule: this.selectedId() ?? 'new',
      range: this.range(),
      style: this.style(),
      condKind: this.condKind(),
      operator: this.operator(),
      operators: this.operatorsFor().map((o) => o.key),
      value: this.value(),
      value2: this.value2(),
      timePeriod: this.timePeriod(),
      formula: this.formula(),
      rankBottom: this.rankBottom(),
      rankPercent: this.rankPercent(),
      rankValue: this.rankValue(),
      fill: this.fill(),
      textColour: this.textColour(),
      shortest: this.shortest(),
      longest: this.longest(),
      barColour: this.barColour(),
      negativeColour: this.negativeColour(),
      gradient: this.gradient(),
      barOnly: this.barOnly(),
      scaleMin: this.scaleMin(),
      scaleMid: this.scaleMid(),
      scaleMax: this.scaleMax(),
      scaleThreePoint: this.scaleThreePoint(),
      iconGroup: this.iconGroup(),
      iconShowValue: this.iconShowValue(),
    };
  }

  /**
   * As the panel's own controls set each field. A colour is one the panel's swatch cycles through; an operator, one
   * the chosen condition offers; `rule`, a rule in the list (as clicking it) or "new" (as "Add rule").
   */
  public uiSet(field: string, value: SheetsUiValue): string | null {
    const text = asText(value);
    const pickFrom = (list: readonly string[], set: (v: string) => void) => setOneOf(field, value, list, set);
    const needText = (set: (v: string) => void) => setText(field, value, set);
    const needFlag = (set: (v: boolean) => void) => setFlag(field, value, set);
    switch (field) {
      case 'rule': {
        if (text === 'new') {
          this.startNew();
          return null;
        }
        const rule = this.rules().find((r) => r.id === text);
        if (!rule) return UI_REASONS.noRule(String(text), this.rules().map((r) => r.id));
        this.selectRule(rule);
        return null;
      }
      case 'range':
        return needText((v) => this.range.set(v));
      case 'style':
        return pickFrom(
          this.styles.map((x) => x.key),
          (v) => this.style.set(v as CfStyle),
        );
      case 'condKind':
        return pickFrom(
          this.condKinds.map((k) => k.key),
          (v) => this.pickCondKind(v as CfConditionKind),
        );
      case 'operator': {
        const offered = this.operatorsFor().map((o) => o.key);
        if (!offered.length) return UI_REASONS.noOperator(this.condKind());
        return pickFrom(offered, (v) => this.operator.set(v));
      }
      case 'value':
        return needText((v) => this.value.set(v));
      case 'value2':
        return needText((v) => this.value2.set(v));
      case 'timePeriod':
        return pickFrom(
          this.timePeriods.map((t) => t.key),
          (v) => this.timePeriod.set(v),
        );
      case 'formula':
        return needText((v) => this.formula.set(v));
      case 'rankBottom':
        return needFlag((v) => this.rankBottom.set(v));
      case 'rankPercent':
        return needFlag((v) => this.rankPercent.set(v));
      case 'rankValue':
        if (text === null || !/^\d+$/.test(text)) return wrongKind(field, UI_REASONS.aWholeNumber);
        this.rankValue.set(text);
        return null;
      case 'fill':
        return pickFrom(FILLS, (v) => this.fill.set(v));
      case 'textColour':
        return pickFrom(['#154CCC', '#0F1F33'], (v) => this.textColour.set(v));
      case 'shortest':
        return pickFrom(['Lowest', 'Number', 'Percent'], (v) => this.shortest.set(v));
      case 'longest':
        return pickFrom(['Highest', 'Number', 'Percent'], (v) => this.longest.set(v));
      case 'barColour':
        return pickFrom(BAR_COLOURS, (v) => this.barColour.set(v));
      case 'negativeColour':
        return pickFrom(['#E40000', '#FF9500'], (v) => this.negativeColour.set(v));
      case 'gradient':
        return needFlag((v) => this.gradient.set(v));
      case 'barOnly':
        return needFlag((v) => this.barOnly.set(v));
      case 'scaleMin':
        return pickFrom(SCALE_LOW, (v) => this.scaleMin.set(v));
      case 'scaleMid':
        return pickFrom(SCALE_MID, (v) => this.scaleMid.set(v));
      case 'scaleMax':
        return pickFrom(SCALE_HIGH, (v) => this.scaleMax.set(v));
      case 'scaleThreePoint':
        return needFlag((v) => this.scaleThreePoint.set(v));
      case 'iconGroup':
        return pickFrom(ICON_GROUPS, (v) => this.iconGroup.set(v));
      case 'iconShowValue':
        return needFlag((v) => this.iconShowValue.set(v));
      default:
        return UI_REASONS.noField('cf', field);
    }
  }

  public uiApply(): string | null {
    this.emitSave();
    return null;
  }

  public uiCancel(): void {
    this.cancelled.emit();
  }

  /** The list's delete, on the rule being edited. */
  public uiRemove(): string | null {
    const id = this.selectedId();
    if (!id) return UI_REASONS.chooseRuleFirst;
    this.startNew();
    this.removeRule.emit(id);
    return null;
  }

  protected emitSave(): void {
    this.save.emit({
      replaceId: this.selectedId(),
      range: this.range(),
      style: this.style(),
      shortest: this.shortest(),
      longest: this.longest(),
      barColour: this.barColour(),
      negativeColour: this.negativeColour(),
      gradient: this.gradient(),
      barOnly: this.barOnly(),
      condKind: this.condKind(),
      operator: this.operator(),
      value: this.value(),
      value2: this.value2(),
      timePeriod: this.timePeriod(),
      formula: this.formula(),
      rankBottom: this.rankBottom(),
      rankPercent: this.rankPercent(),
      rankValue: this.rankValue(),
      fill: this.fill(),
      textColour: this.textColour(),
      scaleMin: this.scaleMin(),
      scaleMid: this.scaleMid(),
      scaleMax: this.scaleMax(),
      scaleThreePoint: this.scaleThreePoint(),
      iconGroup: this.iconGroup(),
      iconShowValue: this.iconShowValue(),
    });
  }
}
